import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'

import EditForm from '../../../../../../features/components/Forms/EditForm'
import MultiSelectBox from '../../../../../../features/components/Form/MultiSelectBox'
import ErrorMessage from '../../../../../../components/Form/ErrorMessage'

import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import {
  getDeletedCriterionsArray,
  removeExistingCriterions
} from '../../../../../../features/helpers/googleCriterionsHelpers'

import { createCampaignCriterions, clearCreateCampaignCriterions } from '../../../../../../modules/actions/campaigns'
import {
  googleLanguagesIsLoadingSelector,
  googleLanguagesSelector
} from '../../../../../../modules/selectors/googleConstants'
import {
  campaignCriterionsSelector,
  createCampaignCriterionsErrorSelector,
  createCampaignCriterionsIsLoadingSelector,
  createCampaignCriterionsWasCreatedSelector
} from '../../../../../../modules/selectors/campaigns'
import { selectedGoogleAdAccountSelector } from '../../../../../../modules/selectors/adAccounts'

import { LANGUAGES } from '../../../../../Multiplatform/LineItemForms/fields'
import { CAMPAIGN_GOOGLE_LANGUAGE_CRITERION_TYPE } from '../../../../../../constants/campaigns'
import { HISTORY_META_EDIT_SECTION, TYPE_CAMPAIGN } from '../../../../../../constants/changeHistory'

const LanguagesSectionForm = ({ editItemData: campaign, title, ...formProps }) => {
  const dispatch = useDispatch()

  const languages = useSelector(googleLanguagesSelector)
  const languagesIsLoading = useSelector(googleLanguagesIsLoadingSelector)
  const { id: selectedAdAccountId } = useSelector(selectedGoogleAdAccountSelector)
  const criterions = useSelector(campaignCriterionsSelector)
  const criterionsWasCreated = useSelector(createCampaignCriterionsWasCreatedSelector)

  const { id: campaignId } = campaign

  const formattedInitialLanguages = useMemo(
    () =>
      criterions
        .filter(criterion => criterion.type_ === CAMPAIGN_GOOGLE_LANGUAGE_CRITERION_TYPE)
        .map(languageCriterion => ({
          label: languages.find(language => language.id === languageCriterion['criterion_id']).name,
          value: languageCriterion['criterion_id'],
          resourceName: languageCriterion.language.language_constant,
          criterionResourceName: languageCriterion.resource_name
        })),
    [criterions, languages]
  )

  const formattedLanguageConstants = useMemo(
    () =>
      languages.map(language => {
        const { id, name, resource_name } = language

        return { value: id, label: name, resourceName: resource_name }
      }),
    [languages]
  )

  const handleSubmit = useCallback(
    values => {
      const criterionsToDelete = getDeletedCriterionsArray({
        initialData: formattedInitialLanguages,
        finalData: values[LANGUAGES]
      })

      const formattedCriterionsData = {
        account: selectedAdAccountId,
        operations: removeExistingCriterions({
          initialData: formattedInitialLanguages,
          finalData: values[LANGUAGES]
        }).map(language => ({
          campaign: campaignId,
          language: {
            language_constant: language.resourceName
          }
        })),
        remove_operations: criterionsToDelete,
        meta: {
          [HISTORY_META_EDIT_SECTION]: title,
          override: {
            item_id: campaignId,
            item_type: TYPE_CAMPAIGN,
            action: 'update'
          }
        }
      }

      dispatch(createCampaignCriterions(formattedCriterionsData, null, criterionsToDelete))
    },
    [dispatch, selectedAdAccountId, formattedInitialLanguages, campaignId, title]
  )

  const initialValues = useMemo(
    () => ({
      [LANGUAGES]: formattedInitialLanguages || []
    }),
    [formattedInitialLanguages]
  )

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit
  })

  const purifiedFormik = usePurifiedFormik(formik)

  const { setFieldValue, values, errors } = purifiedFormik

  const languagesError = errors[LANGUAGES]

  const handleClearCreateCriterions = useCallback(() => {
    dispatch(clearCreateCampaignCriterions())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      clearEditItem={handleClearCreateCriterions}
      successSubmit={criterionsWasCreated}
      errorSelector={createCampaignCriterionsErrorSelector}
      isLoadingSelector={createCampaignCriterionsIsLoadingSelector}
      {...formProps}
    >
      <MultiSelectBox
        placeholder="ADD A LANGUAGE"
        options={formattedLanguageConstants}
        value={values[LANGUAGES]}
        isLoading={languagesIsLoading}
        name={LANGUAGES}
        setFieldValue={setFieldValue}
      />
      {languagesError && <ErrorMessage error={languagesError} />}
    </EditForm>
  )
}

export default LanguagesSectionForm
